import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ViewChild } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
} from '@angular/forms';
import { environment } from '@environments/environment';
import { AuthService } from '@services/auth/auth.service';
import { FileItem, FileUploader, FileUploaderOptions } from 'ng2-file-upload';

interface IConfig {
  type?: 'files' | 'medias';
  onlyView?: boolean; // Default: false
  max?: number; // Default: 1
  title?: string; // Default: 'default'
  description?: string; // Default: 'default'
}

@Component({
  selector: 'app-upload-files',
  templateUrl: 'upload-files.template.html',
  styleUrls: ['./upload-files.style.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: UploadFilesComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: UploadFilesComponent,
    },
  ],
})
export class UploadFilesComponent implements OnInit, ControlValueAccessor {
  @ViewChild('uploadFile', { read: ElementRef }) uploadFile;
  @Output() avatarUrl = new EventEmitter<string>();
  @Output() errorMessage = new EventEmitter<string>();
  @Input() config: IConfig;
  @Input() files: [] = [];
  @Input() showAvatar: boolean;
  @Input() showAvatarUrl: string;
  @Input() disclaimer: string;
  @Input() avatarStyle: boolean = true;
  @Input() touched: boolean;
  @Input() showError: boolean = true;
  @Input() validateError: boolean;
  @Input() noRefresh: boolean = false;
  @Input() requiredBorder: boolean;
  @Input() placeholder: string = 'uploader.uploadphoto';

  public onChange = (dataChange) => {};
  public onTouched = () => {};
  public disabled: boolean = false;

  public uploader: FileUploader;
  public hideInput = false;
  public fileError: string;
  public bgImage: string;
  public hasBaseDropZoneOver = false;
  public gallery: any = [];

  constructor(private authService: AuthService) {}

  ngOnInit() {
    this.config.max = this.config.max ?? 1;
    this.gallery = this.files;
    this.bgImage = this.showAvatarUrl ? `url('${this.showAvatarUrl}')` : 'none';
    if (!this.noRefresh) {
      this.authService.refresh(false);
    }
    this.setupUploader();
  }

  private setupUploader(): void {
    const uploaderConfig: FileUploaderOptions = {
      url: `${environment.api}/files`,
      autoUpload: true,
      queueLimit: this.config.max,
      itemAlias: 'file',
      allowedFileType: this.config.type === 'files' ? ['doc'] : ['image'],
      allowedMimeType:
        this.config.type === 'files'
          ? [
              'text/plain',
              '.csv',
              'application/vnd.ms-excel',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              '.pdf',
            ]
          : ['image/jpeg', 'image/pjpeg', 'image/png'],
      additionalParameter: {
        title: this.config.title ?? 'default',
        description: this.config.description ?? 'default',
      },
    };

    this.uploader = new FileUploader(uploaderConfig);

    if (this.gallery.length === this.config.max) {
      this.hideInput = true;
    }

    this.uploader.onAfterAddingFile = (file) => {
      file.withCredentials = false;
      this.fileError = null;
    };

    this.uploader.onWhenAddingFileFailed = (item, filter, options) => {
      this.fileError = 'errors.forms.uploadfiletype';
      this.errorMessage.emit(this.fileError);
      this.validateError = true;
    };

    this.uploader.onErrorItem = (item, response, status, headers) => {
      this.fileError = 'errors.forms.upload';
      this.errorMessage.emit(this.fileError);
      this.validateError = true;
    };

    this.uploader.onSuccessItem = (item, response, status, headers) => {
      const jsonResponse = JSON.parse(response).data;
      const avatarUrl = jsonResponse.url;
      this.bgImage = `url('${avatarUrl}')`;
      this.gallery.push(jsonResponse);
      this.uploader.clearQueue();
      //this.reset();
      this.avatarUrl.emit(avatarUrl);
      this.errorMessage.emit(null);
      this.validateError = false;
      this.onChange(avatarUrl);
    };

    this.uploader.onBeforeUploadItem = (file: FileItem) => {
      this.uploader.authToken = `Bearer ${this.authService.currentToken}`;
      return file;
    };
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  writeValue(imageURL) {
    if (!imageURL) return;
    this.bgImage = `url('${imageURL}')`;
    this.onChange(this.showAvatarUrl);
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.touched) return;

    const validate = !this.showAvatarUrl;
    this.validateError = validate;

    return validate ? { required: true } : null;
  }

  public fileOverBase(e: any): void {
    this.hasBaseDropZoneOver = e;
    this.hideInput = false;
  }

  private reset() {
    if (this.gallery.length === this.config.max) {
      this.hideInput = true;
    }
    this.uploadFile.nativeElement.value = '';
  }

  public upload(event) {
    if (this.uploader.queue.length > 1) {
      this.uploader.queue[0].remove();
    }
  }

  public delete(fileDelete) {
    this.gallery.forEach((file, index) => {
      if (file.id === fileDelete.id) {
        this.gallery.splice(index, 1);
      }
    });
    if (this.gallery.length < this.config.max) {
      this.hideInput = false;
    }
  }

  public getImages() {
    return this.gallery.reduce(
      (carry, filesGallery) => carry.concat(filesGallery.id),
      [],
    );
  }
}
